import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, resolveMedia } from "@/lib/api";
import { useEntitlements } from "@/lib/entitlements";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { AlertTriangle, CheckCircle2, ChevronRight, Clock, ExternalLink, Info, Lock, Mail, MessageCircle, Search, ShieldCheck, ThumbsDown, ThumbsUp, Loader2, Wrench, ListChecks } from "lucide-react";
import { toast } from "sonner";

/** Texto con **negritas** (formato simple de los artículos). */
export const Rich = ({ text }) => (
  <>{String(text || "").split(/(\*\*[^*]+\*\*)/g).map((p, i) => p.startsWith("**") && p.endsWith("**")
    ? <strong key={i} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong> : <React.Fragment key={i}>{p}</React.Fragment>)}</>
);

const DISP = {
  PARCIAL: { cls: "bg-amber-50 text-amber-800 border-amber-200", label: "Función parcial" },
  PENDIENTE: { cls: "bg-slate-100 text-slate-600 border-slate-200", label: "Función pendiente" },
};

export const fechaRev = (iso) => iso ? new Date(iso).toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "numeric" }) : "—";

export function PlanBadge({ feature }) {
  const ent = useEntitlements();
  if (!feature || ent.loading || ent.has(feature)) return null;
  return (
    <span data-testid="help-plan-badge" className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border bg-violet-50 text-violet-700 border-violet-200">
      <Lock className="w-3 h-3" /> No incluido en tu plan
    </span>
  );
}

export function ArticleCard({ a, onOpen, compact }) {
  return (
    <button data-testid={`help-card-${a.slug}`} onClick={() => onOpen(a.slug)}
      className="group w-full text-left rounded-2xl border border-slate-200 bg-white hover:border-sky-300 hover:shadow-[0_6px_24px_-12px_rgba(14,165,233,0.45)] transition-[border-color,box-shadow] p-4 flex items-start gap-3">
      <span className={`mt-0.5 w-8 h-8 shrink-0 rounded-xl grid place-items-center ${a.tipo === "PROBLEMA" ? "bg-amber-50 text-amber-600" : "bg-sky-50 text-sky-600"}`}>
        {a.tipo === "PROBLEMA" ? <Wrench className="w-4 h-4" /> : <ListChecks className="w-4 h-4" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-semibold text-slate-900 leading-snug group-hover:text-sky-700">{a.titulo}</span>
        {!compact && a.resumen && <span className="block text-sm text-slate-500 mt-0.5 line-clamp-2">{a.resumen}</span>}
        <span className="flex flex-wrap items-center gap-1.5 mt-1.5">
          <span className="text-[11px] text-slate-400">{a.modulo}</span>
          <PlanBadge feature={a.feature} />
        </span>
      </span>
      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-sky-500 mt-1 shrink-0" />
    </button>
  );
}

export function HelpFeedback({ slug, consulta }) {
  const [estado, setEstado] = useState(null);
  const [txt, setTxt] = useState("");
  const enviar = async (util, comentario) => {
    try { await api.post("/help/feedback", { slug, consulta, util, comentario: comentario || undefined }); } catch (_) { /* opcional */ }
  };
  useEffect(() => { setEstado(null); setTxt(""); }, [slug]);
  if (estado === "gracias") return <p data-testid="help-feedback-thanks" className="text-sm text-emerald-700 font-medium flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> ¡Gracias! Tu opinión nos ayuda a mejorar la ayuda.</p>;
  return (
    <div data-testid="help-feedback" className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
      {estado !== "no" ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold text-slate-700">¿Te resultó útil esta información?</span>
          <Button size="sm" variant="outline" data-testid="help-feedback-si" onClick={() => { enviar(true); setEstado("gracias"); }} className="rounded-full"><ThumbsUp className="w-4 h-4 mr-1" /> Sí</Button>
          <Button size="sm" variant="outline" data-testid="help-feedback-no" onClick={() => setEstado("no")} className="rounded-full"><ThumbsDown className="w-4 h-4 mr-1" /> No</Button>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-slate-700">¿Qué faltó? (opcional)</p>
          <Textarea data-testid="help-feedback-comentario" value={txt} maxLength={500} onChange={(e) => setTxt(e.target.value)}
            placeholder="Cuéntanos qué buscabas o qué no quedó claro. No incluyas claves ni datos personales." className="bg-white" />
          <Button size="sm" data-testid="help-feedback-enviar" onClick={() => { enviar(false, txt.trim()); setEstado("gracias"); }} className="bg-slate-900 text-white rounded-full">Enviar</Button>
        </div>
      )}
    </div>
  );
}

function Seccion({ icon: Icon, titulo, tono = "slate", children, testid }) {
  const t = { slate: "text-slate-500", amber: "text-amber-600", sky: "text-sky-600", emerald: "text-emerald-600" }[tono];
  return (
    <section data-testid={testid} className="space-y-2">
      <h3 className={`text-xs font-bold uppercase tracking-widest flex items-center gap-1.5 ${t}`}><Icon className="w-3.5 h-3.5" /> {titulo}</h3>
      {children}
    </section>
  );
}

export const SOPORTE_WA = "56982441644";
export const SOPORTE_EMAIL = "soporte@aipos.cl";

/** Contacto de soporte (WhatsApp y correo publicados en la web principal). */
export function SupportContact({ titulo, slug }) {
  const ref = slug ? `Consulta sobre el artículo de ayuda «${titulo}» (#${slug}): ` : "Hola, necesito ayuda con Ai-POS: ";
  const wa = `https://api.whatsapp.com/send/?phone=${SOPORTE_WA}&text=${encodeURIComponent(ref)}`;
  const mail = `mailto:${SOPORTE_EMAIL}?subject=${encodeURIComponent(slug ? `Ayuda: ${titulo}` : "Ayuda Ai-POS")}&body=${encodeURIComponent(ref)}`;
  return (
    <div data-testid="help-support" className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3">
      <div className="flex-1 min-w-[200px]">
        <p className="text-sm font-semibold text-slate-900">¿No resolviste tu duda?</p>
        <p className="text-xs text-slate-500">Escríbenos a soporte. Nunca compartas claves, PIN ni certificados.</p>
      </div>
      <a href={wa} target="_blank" rel="noreferrer" data-testid="help-support-whatsapp"
        className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-[#25D366] hover:bg-[#1ebe5b] text-white text-sm font-semibold transition-colors">
        <MessageCircle className="w-4 h-4" /> WhatsApp
      </a>
      <a href={mail} data-testid="help-support-email"
        className="inline-flex items-center gap-2 h-10 px-4 rounded-xl border border-slate-200 hover:border-sky-300 hover:bg-sky-50 text-slate-700 text-sm font-semibold transition-colors">
        <Mail className="w-4 h-4" /> {SOPORTE_EMAIL}
      </a>
    </div>
  );
}

export function ArticleView({ a, onOpen, consulta, nuevaPestana }) {
  const ent = useEntitlements();
  const nav = useNavigate();
  const [zoom, setZoom] = useState(null);
  const top = useRef(null);
  useEffect(() => { top.current?.scrollIntoView?.({ block: "start" }); }, [a?.slug]);
  const linkOk = a?.enlace?.ruta && (!a.feature || ent.has(a.feature));
  const disp = DISP[a?.disponibilidad];
  if (!a) return null;
  return (
    <article data-testid={`help-article-${a.slug}`} className="space-y-6" ref={top}>
      <header className="space-y-2">
        <p className="text-xs font-semibold text-sky-600">{a.modulo}</p>
        <h1 data-testid="help-article-title" className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-display leading-tight">{a.titulo}</h1>
        {a.resumen && <p className="text-slate-600">{a.resumen}</p>}
        <div className="flex flex-wrap items-center gap-2">
          {disp && <span data-testid="help-disponibilidad" className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${disp.cls}`}>{disp.label}</span>}
          <PlanBadge feature={a.feature} />
          <span className="text-[11px] text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Revisado {fechaRev(a.revisado_at || a.updated_at)}</span>
          <span className="text-[11px] text-slate-300 font-mono-num">#{a.slug}</span>
        </div>
      </header>

      {a.objetivo && <p className="rounded-2xl bg-sky-50 border border-sky-100 px-4 py-3 text-sm text-sky-900"><strong>Objetivo:</strong> {a.objetivo}</p>}

      {(a.requisitos?.length > 0 || a.permisos) && (
        <Seccion icon={ShieldCheck} titulo="Antes de comenzar" testid="help-requisitos">
          <ul className="space-y-1.5 text-sm text-slate-700">
            {a.requisitos?.map((r, i) => <li key={i} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-sky-500 shrink-0 mt-0.5" /><Rich text={r} /></li>)}
            {a.permisos && <li className="flex gap-2"><Lock className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" /><span><strong>Permisos:</strong> {a.permisos}</span></li>}
          </ul>
        </Seccion>
      )}

      {a.pasos?.length > 0 && (
        <Seccion icon={ListChecks} titulo="Pasos" tono="sky" testid="help-pasos">
          <ol className="space-y-4">
            {a.pasos.map((p, i) => (
              <li key={i} data-testid={`help-paso-${i}`} className="flex gap-3">
                <span className="w-7 h-7 shrink-0 rounded-full bg-slate-900 text-white text-sm font-bold grid place-items-center">{i + 1}</span>
                <div className="min-w-0 flex-1 space-y-2 pt-0.5">
                  <p className="text-slate-700 leading-relaxed"><Rich text={p.texto} /></p>
                  {p.nota && <p className="text-xs text-slate-500 flex gap-1.5"><Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />{p.nota}</p>}
                  {p.imagen && (
                    <button onClick={() => setZoom(p.imagen)} className="block w-full rounded-xl overflow-hidden border border-slate-200 bg-slate-50 hover:border-sky-300 transition-colors">
                      <img src={resolveMedia(p.imagen)} alt={`Paso ${i + 1}: ${a.titulo}`} loading="lazy" className="w-full max-h-[420px] object-contain" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </Seccion>
      )}

      {a.resultado && (
        <Seccion icon={CheckCircle2} titulo="Resultado esperado" tono="emerald">
          <p className="text-sm text-slate-700"><Rich text={a.resultado} /></p>
        </Seccion>
      )}

      {a.errores?.length > 0 && (
        <Seccion icon={AlertTriangle} titulo="Errores habituales y soluciones" tono="amber" testid="help-errores">
          <div className="space-y-2">
            {a.errores.map((e, i) => (
              <div key={i} className="rounded-2xl border border-amber-200 bg-amber-50/60 p-3 text-sm">
                <p className="font-semibold text-slate-900">{e.sintoma}</p>
                {e.causa && <p className="text-slate-600 mt-0.5"><span className={`text-[10px] font-bold uppercase mr-1.5 px-1.5 py-0.5 rounded ${e.confirmada ? "bg-amber-200 text-amber-900" : "bg-white text-slate-500 border border-slate-200"}`}>{e.confirmada ? "Causa confirmada" : "Causa posible"}</span>{e.causa}</p>}
                <p className="text-slate-700 mt-1"><strong>Solución:</strong> <Rich text={e.solucion} /></p>
              </div>
            ))}
          </div>
        </Seccion>
      )}

      {a.restricciones && (
        <div data-testid="help-restricciones" className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 flex gap-2">
          <Info className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" /><span><strong>Importante:</strong> <Rich text={a.restricciones} /></span>
        </div>
      )}

      {linkOk && (
        nuevaPestana
          ? <a href={a.enlace.ruta} target="_blank" rel="noreferrer" data-testid="help-article-link" className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold transition-colors">{a.enlace.label || "Abrir módulo"} <ExternalLink className="w-4 h-4" /></a>
          : <Button data-testid="help-article-link" onClick={() => nav(a.enlace.ruta)} className="rounded-xl bg-sky-600 hover:bg-sky-700 text-white">{a.enlace.label || "Abrir módulo"} <ChevronRight className="w-4 h-4 ml-1" /></Button>
      )}

      {a.relacionados_info?.length > 0 && (
        <Seccion icon={Search} titulo="Artículos relacionados">
          <div className="grid sm:grid-cols-2 gap-2">{a.relacionados_info.map((r) => <ArticleCard key={r.slug} a={r} onOpen={onOpen} compact />)}</div>
        </Seccion>
      )}

      <HelpFeedback slug={a.slug} consulta={consulta} />
      <SupportContact titulo={a.titulo} slug={a.slug} />

      <Dialog open={!!zoom} onOpenChange={(o) => !o && setZoom(null)}>
        <DialogContent className="max-w-6xl p-2 bg-white"><img src={resolveMedia(zoom)} alt="Captura ampliada" className="w-full h-auto rounded-lg" /></DialogContent>
      </Dialog>
    </article>
  );
}

export const EJEMPLOS = [
  "¿Cómo genero una nota de crédito?", "¿Cómo hago una factura electrónica?", "Se me cayó internet, ¿puedo seguir vendiendo?",
  "¿Cómo cierro la caja?", "¿Cómo veo las ventas de ayer?", "¿Cómo configuro una impresora térmica?",
];

/** Buscador en lenguaje natural con respuesta directa y resultados. */
export function useHelpSearch(ruta) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);
  const buscar = async (texto) => {
    const t = (texto ?? q).trim();
    setQ(texto ?? q);
    if (t.length < 2) { setRes(null); return; }
    const n = ++seq.current;
    setBusy(true);
    try {
      const { data } = await api.get("/help/search", { params: { q: t, ruta } });
      if (n === seq.current) setRes({ ...data, q: t });
    } catch (e) {
      if (n === seq.current) toast.error(e?.response?.data?.detail || "No se pudo buscar");
    } finally { if (n === seq.current) setBusy(false); }
  };
  return { q, setQ, res, setRes, busy, buscar };
}

export function SearchBox({ s, big, autoFocus }) {
  return (
    <form onSubmit={(e) => { e.preventDefault(); s.buscar(); }} className="relative" data-testid="help-search-form">
      <Search className={`absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 ${big ? "w-6 h-6" : "w-5 h-5"}`} />
      <input data-testid="help-search-input" autoFocus={autoFocus} value={s.q} onChange={(e) => s.setQ(e.target.value)} maxLength={200}
        placeholder="¿En qué podemos ayudarte?"
        className={`w-full rounded-2xl bg-white border border-slate-200 focus:border-sky-400 focus:ring-4 focus:ring-sky-100 outline-none text-slate-900 placeholder:text-slate-400 transition-[border-color,box-shadow] ${big ? "h-16 pl-14 pr-32 text-lg shadow-[0_10px_40px_-20px_rgba(15,23,42,0.35)]" : "h-12 pl-12 pr-24"}`} />
      <Button type="submit" data-testid="help-search-btn" disabled={s.busy}
        className={`absolute right-2 top-1/2 -translate-y-1/2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white ${big ? "h-12 px-5" : "h-9 px-3"}`}>
        {s.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Buscar"}
      </Button>
    </form>
  );
}

export function SearchResults({ s, onOpen }) {
  const r = s.res;
  const otros = useMemo(() => (r?.items || []).filter((x) => x.slug !== r?.respuesta?.slug), [r]);
  if (!r) return null;
  return (
    <div data-testid="help-search-results" className="space-y-4">
      {r.corregidas?.length > 0 && <p className="text-xs text-slate-500">Buscamos también: <em>{r.corregidas.join(", ")}</em></p>}
      {!r.items.length && (
        <div data-testid="help-no-results" className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600 space-y-2">
          <p className="font-semibold text-slate-900">No encontramos un artículo para «{r.q}».</p>
          <p>Prueba con otras palabras (por ejemplo, el nombre de la pantalla o la acción: «cerrar caja», «factura», «impresora»). Si es algo que no existe en Ai-POS te lo indicaremos en el artículo correspondiente.</p>
          <HelpFeedback consulta={r.q} />
          <SupportContact />
        </div>
      )}
      {r.respuesta && (
        <div data-testid="help-direct-answer" className="rounded-3xl border-2 border-sky-200 bg-white p-5 sm:p-6 shadow-[0_10px_40px_-24px_rgba(14,165,233,0.6)]">
          <p className="text-[11px] font-bold uppercase tracking-widest text-sky-600 mb-3">Mejor respuesta</p>
          <ArticleView a={r.respuesta} onOpen={onOpen} consulta={r.q} />
        </div>
      )}
      {r.ambigua && <p data-testid="help-ambigua" className="text-sm text-slate-600 flex items-center gap-1.5"><Info className="w-4 h-4 text-sky-500" /> Encontramos varias opciones. Elige la que mejor describe lo que necesitas:</p>}
      {otros.length > 0 && (
        <div className="space-y-2">
          {r.respuesta && <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Otros artículos</p>}
          {otros.map((a) => <ArticleCard key={a.slug} a={a} onOpen={onOpen} />)}
        </div>
      )}
    </div>
  );
}
